// Sample data (specs 32): the Home banner while it's there, the "Explore
// with sample data" card for a new store, "Sample" badges in lists, and the
// "Show sample data in reports" switch.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Shirt, Smartphone, ShoppingBasket, Sparkles } from "lucide-react";
import { api, storePath } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useStore } from "../lib/store";
import { Badge, Banner, Button, Card, cx, Modal, Switch, useToast } from "./ui";
import type { Key } from "../locales/en";

export type SampleType = "product" | "customer" | "order" | "collection" | "page" | "article" | "discount";

export type SampleStatus = {
  present: boolean;
  batch: { id: string; industry: string; status: "seeding" | "ready" | "failed" | "removing" | "removed"; counts: Record<string, number>;
    kept?: Record<string, string[]>; error?: string; created_at: string } | null;
  ids: Partial<Record<SampleType, string[]>>;
  edited: Partial<Record<string, number>>;
  show_in_reports: boolean;
  can_add: boolean;
  industries: string[];
};

const key = (id?: string) => ["store", id, "/sample-data"];

// useSampleData polls while sample data is being added or removed.
export function useSampleData() {
  const { id } = useStore();
  return useQuery({
    queryKey: key(id), enabled: !!id, staleTime: 30_000,
    queryFn: () => api<SampleStatus>(storePath(id!, "/sample-data")),
    refetchInterval: (q) => (q.state.data?.batch?.status === "seeding" || q.state.data?.batch?.status === "removing" ? 3000 : false),
  });
}

// useSampleIds: the sample rows of one type, for badges.
export function useSampleIds(type: SampleType): Set<string> {
  const q = useSampleData();
  return useMemo(() => new Set(q.data?.present ? q.data.ids[type] ?? [] : []), [q.data, type]);
}

export function SampleBadge({ show }: { show: boolean }) {
  const { t } = useI18n();
  return show ? <Badge tone="info">{t("sample.badge")}</Badge> : null;
}

function useRefreshAll() {
  const qc = useQueryClient();
  const { id } = useStore();
  // Everything sample data touches: lists, the guide, reports.
  return () => void qc.invalidateQueries({ queryKey: ["store", id] });
}

// SampleBanner: on Home while the store has sample data (or it's on its way).
export function SampleBanner() {
  const { t } = useI18n();
  const { summary } = useStore();
  const q = useSampleData();
  const [removing, setRemoving] = useState(false);
  const canManage = summary.data?.me.permissions.includes("settings.general") ?? false;
  const b = q.data?.batch;
  if (!q.data || !b) return null;
  if (b.status === "seeding") {
    return <Banner tone="info">{t("sample.seeding")}</Banner>;
  }
  if (b.status === "removing") {
    return <Banner tone="info">{t("sample.removing")}</Banner>;
  }
  if (b.status === "failed" && !q.data.present) {
    return <Banner tone="warning">{t("sample.failed")}</Banner>;
  }
  if (!q.data.present) return null;
  const counts = b.counts;
  return (
    <Card>
      <div className="flex flex-wrap items-start gap-4">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-info-bg text-info"><FlaskConical className="size-5" /></span>
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-sm font-semibold text-ink">{t("sample.banner_title")}</h2>
          <p className="text-[13px] text-ink-muted">
            {t("sample.banner_body", { products: counts.product ?? 0, orders: counts.order ?? 0, customers: counts.customer ?? 0 })}
          </p>
          {b.error && <p className="text-[13px] text-danger">{t("sample.remove_failed")}</p>}
        </div>
        {canManage && <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto sm:shrink-0">
          <ReportsSwitch />
          <Button onClick={() => setRemoving(true)}>{t("sample.remove")}</Button>
        </div>}
      </div>
      {removing && <RemoveDialog status={q.data} onClose={() => setRemoving(false)} />}
    </Card>
  );
}

// ReportsSwitch: "Show sample data in reports".
export function ReportsSwitch({ withHint }: { withHint?: boolean }) {
  const { t } = useI18n();
  const { id } = useStore();
  const q = useSampleData();
  const refresh = useRefreshAll();
  const toast = useToast();
  const set = useMutation({
    mutationFn: (show: boolean) => api<SampleStatus>(storePath(id!, "/sample-data/settings"), { method: "PUT", body: { show_in_reports: show } }),
    onSuccess: (_, show) => { refresh(); toast(t(show ? "sample.reports_on" : "sample.reports_off")); },
  });
  if (!q.data?.present) return null;
  return (
    <label className="flex items-center gap-2 text-[13px] text-ink-muted">
      <Switch checked={q.data.show_in_reports} disabled={set.isPending} label={t("sample.show_in_reports")} onChange={(v) => set.mutate(v)} />
      <span>{t("sample.show_in_reports")}{withHint && <span className="block text-xs">{t("sample.show_in_reports_hint")}</span>}</span>
    </label>
  );
}

const labels: Record<string, Key> = { product: "sample.kind.product", collection: "sample.kind.collection", page: "sample.kind.page",
  article: "sample.kind.article", discount: "sample.kind.discount", blog: "sample.kind.blog" };

export function RemoveDialog({ status, onClose }: { status: SampleStatus; onClose: () => void }) {
  const { t } = useI18n();
  const { id } = useStore();
  const refresh = useRefreshAll();
  const edited = Object.entries(status.edited).filter(([, n]) => (n ?? 0) > 0);
  const [keep, setKeep] = useState(true);
  const remove = useMutation({
    mutationFn: () => api(storePath(id!, `/sample-data?keep_edited=${edited.length > 0 && keep}`), { method: "DELETE" }),
    onSuccess: () => { refresh(); onClose(); },
  });
  return (
    <Modal open title={t("sample.remove_title")} onClose={onClose} size="sm"
      footer={<>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="critical" loading={remove.isPending} onClick={() => remove.mutate()}>{t("sample.remove")}</Button>
      </>}>
      <div className="space-y-3 text-sm">
        <p className="text-ink-muted">{t("sample.remove_body")}</p>
        {edited.length > 0 && (
          <label className="flex items-start gap-3 rounded-xl bg-muted p-3">
            <input type="checkbox" className="mt-0.5" checked={keep} onChange={(e) => setKeep(e.target.checked)} />
            <span>
              <span className="block font-medium text-ink">{t("sample.keep_edited")}</span>
              <span className="block text-[13px] text-ink-muted">
                {edited.map(([k, n]) => `${n} ${t(labels[k] ?? "sample.kind.product")}`).join(", ")}
              </span>
            </span>
          </label>
        )}
        {remove.error && <Banner tone="critical">{remove.error.message}</Banner>}
      </div>
    </Modal>
  );
}

const industryIcons = { fashion: Shirt, electronics: Smartphone, grocery: ShoppingBasket, beauty: Sparkles } as const;

// ExploreCard: a new store (no products, no orders) can try a full store.
export function ExploreCard() {
  const { t } = useI18n();
  const { id, summary } = useStore();
  const q = useSampleData();
  const refresh = useRefreshAll();
  const [industry, setIndustry] = useState("fashion");
  const add = useMutation({
    mutationFn: () => api(storePath(id!, "/sample-data"), { method: "POST", body: { industry } }),
    onSuccess: refresh,
  });
  const canManage = summary.data?.me.permissions.includes("settings.general") ?? false;
  if (!q.data?.can_add || !canManage || q.data.batch?.status === "removed") return null;
  return (
    <Card title={t("sample.explore_title")}>
      <p className="mb-4 text-[13px] text-ink-muted">{t("sample.explore_body")}</p>
      <div role="radiogroup" aria-label={t("sample.industry")} className="grid gap-2 sm:grid-cols-4">
        {(q.data.industries.length ? q.data.industries : ["fashion", "electronics", "grocery", "beauty"]).map((k) => {
          const Icon = industryIcons[k as keyof typeof industryIcons] ?? Shirt;
          return (
            <button key={k} type="button" role="radio" aria-checked={industry === k} onClick={() => setIndustry(k)}
              className={cx("flex items-center gap-2 rounded-xl px-3 py-2.5 text-start text-sm ring-1 transition",
                industry === k ? "bg-brand-tint font-medium text-ink ring-brand" : "ring-line hover:bg-muted")}>
              <Icon className="size-4 shrink-0 text-ink-muted" />{t(`sample.industry.${k}` as Key)}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ink-muted">{t("sample.explore_note")}</p>
        <Button variant="primary" loading={add.isPending} onClick={() => add.mutate()}>{t("sample.add")}</Button>
      </div>
      {add.error && <div className="mt-3"><Banner tone="critical">{add.error.message}</Banner></div>}
    </Card>
  );
}

// SampleReportsNotice: on analytics pages while the store has sample data.
export function SampleReportsNotice() {
  const { t } = useI18n();
  const { summary } = useStore();
  const q = useSampleData();
  if (!q.data?.present) return null;
  const canManage = summary.data?.me.permissions.includes("settings.general") ?? false;
  return (
    <Banner tone="info" action={canManage ? <ReportsSwitch /> : undefined}>
      {t(q.data.show_in_reports ? "sample.shown_in_reports" : "sample.hidden_in_reports")}
    </Banner>
  );
}
